import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { installFetchLoopGuard } from './lib/loopGuard';
import { bootImpersonate, getImpersonate } from './lib/impersonate';
import { reportError, isReportUrl, apiRoute } from './lib/reportError';
import { installDomResilience, isDomMutationRace } from './lib/domResilience';
import { isV2 } from './lib/uiVersion';

// /V2 is the desktop redesign (src/v2/README.md). Lazy so the classic app at
// / never downloads it; the data-ui attribute is what scopes v2/theme.css.
const AppV2 = React.lazy(() => import('./v2/AppV2'));
const UI = isV2() ? 'v2' : 'v1';
document.documentElement.dataset.ui = UI;

// Immunity to browser extensions (Translate, Grammarly, password managers)
// reparenting nodes React owns — otherwise React's removeChild/insertBefore
// throws NotFoundError and a whole subtree crashes to a boundary. Must be
// installed BEFORE React mounts so the patched prototypes are what the
// reconciler calls. See lib/domResilience.js.
installDomResilience();

// Self-diagnosing fetch-loop circuit breaker. Catches accidental useEffect
// runaways before they spam the server + freeze the UI. See lib/loopGuard.js.
installFetchLoopGuard();

// Admin impersonation boot. When the URL carries `?as=<mailbox>`, this
// flips the tab into "viewing another mailbox" mode by stamping a
// header on every /api/* call below. Must run BEFORE the fetch
// interceptor + before App mounts so the very first /api/auth/me
// already carries the override.
bootImpersonate();

// Catch raw throws and unhandled promise rejections that escape React.
// Without these, the only trace of a crash is a brief console message that
// vanishes on reload. These cover only errors NOBODY catches — caught /api
// failures report themselves once, via the fetch interceptor below.
window.addEventListener('error', e => {
  reportError({
    source:  'window.error',
    message: e?.message || String(e),
    stack:   e?.error?.stack,
    route:   e?.filename ? `${e.filename}:${e.lineno || 0}` : undefined,
  });
});
window.addEventListener('unhandledrejection', e => {
  const r = e?.reason;
  reportError({
    source:  'unhandledrejection',
    message: (r && (r.message || String(r))) || 'unknown',
    stack:   r?.stack,
  });
});

// Top-level boundary. Without this, a single throw anywhere in the tree
// unmounts the whole app and the user sees a white page. Now they see a
// "Something went wrong" panel with a reload button, and we get the
// stack on the server.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // A reparented-node race (browser extension) is recoverable: the DOM shim
    // absorbs it at source, but if one ever reaches here, re-render ONCE
    // rather than showing a dead page — React reconciles cleanly afterwards.
    // Report it as informational (not a crash) so it can't count as actionable.
    if (isDomMutationRace(error) && !this._domRetried) {
      this._domRetried = true;
      reportError({ source: 'dom.mutation', message: `Recovered from external DOM mutation at root: ${error?.message || error}` });
      setTimeout(() => this.setState({ error: null }), 0);
      return;
    }
    reportError({
      source:  'react.boundary',
      message: error?.message || String(error),
      // The component stack names the component that blew up; far more useful
      // for grouping than a minified JS frame.
      stack:   [error?.stack, info?.componentStack].filter(Boolean).join('\n--- componentStack ---\n'),
    });
  }
  render() {
    if (this.state.error) {
      return React.createElement('div', {
        style: {
          padding: '24px',
          maxWidth: '560px',
          margin: '80px auto',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#1f2937',
        }
      },
        React.createElement('h2', { style: { marginTop: 0 } }, 'Something went wrong'),
        React.createElement('p', null, 'The page hit an error and stopped. The details have been logged. Reload to continue.'),
        React.createElement('button', {
          onClick: () => window.location.reload(),
          style: {
            padding: '8px 16px',
            background: '#2563eb',
            color: '#fff',
            border: 0,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }
        }, 'Reload'),
        React.createElement('pre', {
          style: {
            marginTop: '24px',
            padding: '12px',
            background: '#f3f4f6',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#6b7280',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }
        }, String(this.state.error?.message || this.state.error))
      );
    }
    return this.props.children;
  }
}

// Auth interceptor: any /api/* response with a 401 means the session
// expired (cookie missing, JWT past TTL, server restart with new
// SESSION_SECRET). Dispatch a single event the App listens for and
// drops back to the login screen. Skip /api/auth/me + /api/auth/login
// because their 401 is the intentional "show me the login form" path,
// not an expiry mid-session.
(function installAuthInterceptor() {
  if (typeof window === 'undefined' || window.__ppAuthInterceptorInstalled) return;
  window.__ppAuthInterceptorInstalled = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    // Admin impersonation: stamp the per-request override header on
    // any /api/* call. The server gates this on session.admin === 1
    // and validates the target against the OnPoint staff list.
    try {
      const impersonate = getImpersonate();
      if (impersonate?.mailbox) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.includes('/api/')) {
          const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input?.headers) || {});
          headers.set('X-Impersonate-Mailbox', impersonate.mailbox);
          init = { ...(init || {}), headers };
        }
      }
    } catch { /* never let impersonation header errors break a request */ }
    // Exempt only the reporter's OWN POST from reporting — otherwise a failing
    // report reports its own failure, forever. The badge's GET /count and the
    // panel's GET list are NOT exempt, so an outage of the error pipeline
    // itself is still visible.
    const reqUrl = typeof input === 'string' ? input : (input && input.url) || '';
    const isReporterPost = isReportUrl(reqUrl) &&
      String(init?.method || 'GET').toUpperCase() === 'POST';

    let res;
    try {
      res = await origFetch(input, init);
    } catch (err) {
      // Aborted requests are intentional, not errors: the autosave aborts its
      // in-flight POST on every new save, the search box aborts on each
      // keystroke. Reporting them would manufacture an error row per keystroke.
      // Every other abort site in the app stays silent; so does this.
      if (err?.name !== 'AbortError' && reqUrl.includes('/api/') && !isReporterPost) {
        reportError({
          source:  'fetch-network',
          message: err?.message || 'network error',
          route:   apiRoute(reqUrl),
          stack:   err?.stack,
        });
      }
      throw err;
    }
    try {
      const url = reqUrl;
      if (
        res.status === 401 &&
        url.includes('/api/') &&
        !url.includes('/api/auth/me') &&
        !url.includes('/api/auth/login')
      ) {
        window.dispatchEvent(new CustomEvent('pp:session-expired'));
      }
      if (url.includes('/api/') && !isReporterPost) {
        const route = apiRoute(url);
        // Report SERVER faults only. 4xx are client/validation answers the UI
        // already handles inline — 401 (session expiry, above), 404 (expected,
        // recovered from), 422 (price-drift, unknown-customer), 413 (oversize
        // inline image), 403 — so reporting them buries real 500s and fills the
        // badge with non-bugs.
        //
        // 503 is the same story on the server side: it's the honest code for a
        // *transient* — Graph throttling / mailbox-slot-queue full, or the
        // brief deploy window where index.html is being rewritten. These
        // self-heal and the UI recovers on the next poll/retry, so counting
        // them as actionable buried the real 500s and topped the daily digest
        // with throttle noise. Exclude 503 (but keep 500/502/504+ — those are
        // genuine faults or Microsoft-side timeouts worth seeing).
        if (res.status >= 500 && res.status !== 503) {
          reportError({
            source:  'fetch',
            message: `HTTP ${res.status} on ${init?.method || 'GET'} ${route}`,
            route,
            status:  res.status,
          });
        }
        // Self-diagnosing JSON parse. An /api/* call that answers with HTML
        // (SPA catch-all serving index.html with a 200) makes res.json() die
        // with a bare "Unexpected token '<', "<!DOCTYPE "…". Wrap res.json at
        // the one choke point every request passes through. Only fires if a
        // caller calls .json(), so binary consumers are untouched.
        const origJson = res.json.bind(res);
        res.json = async () => {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('json')) return origJson();
          const body = await res.text().catch(() => '');
          const message =
            `Expected JSON from ${route} — got ${ct || 'no content-type'} ` +
            `(HTTP ${res.status}): ${body.slice(0, 80).replace(/\s+/g, ' ').trim()}`;
          // Only report when the STATUS looked OK but the body isn't JSON (the
          // 200-HTML case). A non-ok status was already reported above as a
          // 5xx; its json-parse failure is a consequence, not a second bug —
          // reporting it here is what triple-counted one failure.
          if (res.ok) reportError({ source: 'fetch-json', message, route, status: res.status });
          throw new Error(message);
        };
      }
    } catch { /* never let interceptor errors break the response chain */ }
    return res;
  };
})();

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    {UI === 'v2'
      ? <React.Suspense fallback={null}><AppV2 /></React.Suspense>
      : <App />}
  </ErrorBoundary>
);
